'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import ChannelBadge from '@/components/ui/ChannelBadge';
import PriorityBadge from '@/components/ui/PriorityBadge';
import SentimentBadge, { SentimentType } from '@/components/ui/SentimentBadge';
import StatusBadge from '@/components/ui/StatusBadge';

type ChannelFilter = 'All' | 'Voice' | 'WhatsApp' | 'Live Chat' | 'Email' | 'Web Chat' | 'GovChat';
type StatusFilter = 'All' | 'Active' | 'Waiting' | 'Resolved';

interface Interaction {
  id: string;
  channel: 'PHONE' | 'WHATSAPP' | 'LIVE CHAT' | 'EMAIL' | 'WEB CHAT' | 'GOVCHAT';
  citizen: string;
  topic: string;
  agency: string;
  time: string;
  duration: string;
  agent: string;
  status: string;
  priority: 'Critical' | 'High' | 'Medium' | 'Low';
  sentiment: SentimentType;
  aiStatus: string;
  queue: string;
}

const allInteractions: Interaction[] = [
  { id: 'INT-2026-14892', channel: 'PHONE', citizen: 'Ahmed Firdaus', topic: 'MySejahtera Mobile Number Update', agency: 'KKM', time: '14:32', duration: '06:43', agent: 'Nur Aisyah', status: 'Active', priority: 'High', sentiment: 'Concerned', aiStatus: 'Analysed', queue: 'MyGCC General' },
  { id: 'INT-2026-14891', channel: 'WHATSAPP', citizen: 'Nurul Huda', topic: 'Passport Renewal Documentation', agency: 'JIM', time: '14:28', duration: '00:12', agent: 'Nur Aisyah', status: 'Active', priority: 'Medium', sentiment: 'Frustrated', aiStatus: 'Analysed', queue: 'Immigration' },
  { id: 'INT-2026-14890', channel: 'LIVE CHAT', citizen: 'Daniel Tan', topic: 'STR Eligibility Enquiry', agency: 'KPKT', time: '14:25', duration: '00:07', agent: 'Nur Aisyah', status: 'Active', priority: 'Medium', sentiment: 'Neutral', aiStatus: 'Analysed', queue: 'Government Assistance' },
  { id: 'INT-2026-14889', channel: 'EMAIL', citizen: 'Siti Mariam', topic: 'Government Assistance Application Follow-up', agency: 'KPKT', time: '14:20', duration: '—', agent: 'Nur Aisyah', status: 'Waiting', priority: 'Low', sentiment: 'Concerned', aiStatus: 'Pending', queue: 'General' },
  { id: 'INT-2026-14888', channel: 'PHONE', citizen: 'Kumar Rajan', topic: 'Road Tax Renewal', agency: 'JPJ', time: '14:15', duration: '04:21', agent: 'Hafizuddin Razali', status: 'Resolved', priority: 'Medium', sentiment: 'Satisfied', aiStatus: 'Complete', queue: 'Transport' },
  { id: 'INT-2026-14887', channel: 'WEB CHAT', citizen: 'Nur Aina', topic: 'MyDigital ID Registration Assistance', agency: 'JPN', time: '14:10', duration: '08:15', agent: 'Siti Rohaida', status: 'Resolved', priority: 'Low', sentiment: 'Positive', aiStatus: 'Complete', queue: 'Digital Services' },
  { id: 'INT-2026-14886', channel: 'GOVCHAT', citizen: 'Muhammad Danish', topic: 'KWSP Withdrawal Eligibility', agency: 'KWSP', time: '14:05', duration: '05:32', agent: 'Ahmad Shahril', status: 'Resolved', priority: 'High', sentiment: 'Neutral', aiStatus: 'Complete', queue: 'Finance' },
  { id: 'INT-2026-14885', channel: 'WHATSAPP', citizen: 'Farah Lim', topic: 'Income Tax Filing Assistance', agency: 'LHDNM', time: '13:58', duration: '—', agent: 'Nurul Faizah', status: 'Waiting', priority: 'Medium', sentiment: 'Frustrated', aiStatus: 'Analysed', queue: 'Taxation' },
  { id: 'INT-2026-14884', channel: 'EMAIL', citizen: 'Azri Hamdan', topic: 'MySejahtera Account Recovery', agency: 'KKM', time: '13:45', duration: '—', agent: 'Mohd Hafiz', status: 'Resolved', priority: 'Low', sentiment: 'Satisfied', aiStatus: 'Complete', queue: 'Health Services' },
  { id: 'INT-2026-14883', channel: 'PHONE', citizen: 'Priya Sundaram', topic: 'Passport Application Status', agency: 'JIM', time: '13:30', duration: '07:18', agent: 'Lim Bee Eng', status: 'Resolved', priority: 'High', sentiment: 'Angry', aiStatus: 'Complete', queue: 'Immigration' },
  { id: 'INT-2026-14882', channel: 'LIVE CHAT', citizen: 'Zulkifli Othman', topic: 'PERKESO Claim Submission', agency: 'PERKESO', time: '13:22', duration: '11:04', agent: 'Rajasegaran M.', status: 'Resolved', priority: 'High', sentiment: 'Neutral', aiStatus: 'Complete', queue: 'Finance' },
  { id: 'INT-2026-14881', channel: 'GOVCHAT', citizen: 'Mei Ling Tan', topic: 'STR Application Status', agency: 'KPKT', time: '13:15', duration: '03:45', agent: 'Nur Aisyah', status: 'Resolved', priority: 'Medium', sentiment: 'Satisfied', aiStatus: 'Complete', queue: 'Government Assistance' },
];

const channelMap: Record<ChannelFilter, string | null> = {
  All: null, Voice: 'PHONE', WhatsApp: 'WHATSAPP', 'Live Chat': 'LIVE CHAT',
  Email: 'EMAIL', 'Web Chat': 'WEB CHAT', GovChat: 'GOVCHAT',
};

const statusMap: Record<StatusFilter, string | null> = {
  All: null, Active: 'Active', Waiting: 'Waiting', Resolved: 'Resolved',
};

export default function InteractionsContent() {
  const [channelFilter, setChannelFilter] = useState<ChannelFilter>('All');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('All');
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<keyof Interaction>('time');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selectedInteraction, setSelectedInteraction] = useState<Interaction | null>(null);

  const filtered = allInteractions.filter(i => {
    const matchChannel = !channelMap[channelFilter] || i.channel === channelMap[channelFilter];
    const matchStatus = !statusMap[statusFilter] || i.status === statusMap[statusFilter];
    const matchSearch = !search || i.citizen.toLowerCase().includes(search.toLowerCase()) ||
      i.topic.toLowerCase().includes(search.toLowerCase()) || i.id.toLowerCase().includes(search.toLowerCase());
    return matchChannel && matchStatus && matchSearch;
  });

  const sorted = [...filtered].sort((a, b) => {
    const av = String(a[sortField]);
    const bv = String(b[sortField]);
    return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  });

  const handleSort = (field: keyof Interaction) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const counts = {
    active: allInteractions.filter(i => i.status === 'Active').length,
    waiting: allInteractions.filter(i => i.status === 'Waiting').length,
    resolved: allInteractions.filter(i => i.status === 'Resolved').length,
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-5 py-4 page-transition">
      {/* Interaction Detail View */}
      {selectedInteraction && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 p-4">
          <div className="bg-card border border-border rounded-2xl shadow-modal w-full max-w-[600px] max-h-[90vh] overflow-y-auto slide-in-up">
            <div className="flex items-center justify-between p-5 border-b border-border">
              <div>
                <div className="text-[11px] font-700 text-primary mb-0.5">{selectedInteraction.id}</div>
                <h2 className="text-[16px] font-700 text-foreground">{selectedInteraction.topic}</h2>
              </div>
              <button onClick={() => setSelectedInteraction(null)} className="p-2 rounded-lg hover:bg-muted transition-colors">
                <Icon name="XMarkIcon" size={16} className="text-muted-foreground" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3 text-[12px]">
                {[
                  { label: 'Citizen', value: selectedInteraction.citizen },
                  { label: 'Channel', value: selectedInteraction.channel },
                  { label: 'Agency', value: selectedInteraction.agency },
                  { label: 'Queue', value: selectedInteraction.queue },
                  { label: 'Agent', value: selectedInteraction.agent },
                  { label: 'Time', value: selectedInteraction.time },
                  { label: 'Duration', value: selectedInteraction.duration },
                  { label: 'Status', value: selectedInteraction.status },
                ].map(f => (
                  <div key={f.label} className="bg-muted/40 rounded-xl px-3 py-2">
                    <div className="text-muted-foreground text-[10px]">{f.label}</div>
                    <div className="font-600 text-foreground">{f.value}</div>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-3">
                <StatusBadge status={selectedInteraction.status} />
                <PriorityBadge priority={selectedInteraction.priority} />
                <SentimentBadge sentiment={selectedInteraction.sentiment} showBg />
              </div>
              <div className="bg-ai-bg border border-ai-border rounded-xl p-3">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Icon name="SparklesIcon" size={12} className="text-primary" />
                  <span className="text-[10px] font-700 text-primary uppercase tracking-wide">CX AI Analysis</span>
                </div>
                <div className="text-[12px] text-foreground">
                  <span className="font-600">Intent:</span> {selectedInteraction.topic} ·{' '}
                  <span className="font-600">Sentiment:</span> {selectedInteraction.sentiment} ·{' '}
                  <span className="font-600">AI Status:</span> {selectedInteraction.aiStatus}
                </div>
              </div>
              <div className="flex gap-3">
                <Link href="/active-interaction-workspace" className="flex-1">
                  <button className="w-full btn-primary py-2.5 text-[13px] flex items-center justify-center gap-2">
                    <Icon name="ArrowTopRightOnSquareIcon" size={14} />
                    Open Workspace
                  </button>
                </Link>
                <button onClick={() => { setSelectedInteraction(null); toast.info(`Linking case to ${selectedInteraction.id}`); }} className="flex-1 btn-secondary py-2.5 text-[13px]">
                  Link Case
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-[18px] font-700 text-foreground">Interactions</h1>
          <p className="text-[12px] text-muted-foreground mt-0.5">
            Omnichannel interaction inbox · {filtered.length} interactions
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-ghost px-3 py-1.5 border border-border flex items-center gap-1.5 text-[12px]">
            <Icon name="ArrowDownTrayIcon" size={13} />
            Export
          </button>
          <button
            onClick={() => toast.info('New interaction created')}
            className="btn-primary px-3 py-1.5 flex items-center gap-1.5 text-[12px]"
          >
            <Icon name="PlusIcon" size={13} />
            New Interaction
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        {[
          { label: 'Active', count: counts.active, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', dot: 'bg-red-500' },
          { label: 'Waiting', count: counts.waiting, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', dot: 'bg-amber-500' },
          { label: 'Resolved Today', count: counts.resolved, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200', dot: 'bg-green-500' },
        ].map(s => (
          <button
            key={s.label}
            onClick={() => setStatusFilter(s.label === 'Resolved Today' ? 'Resolved' : s.label as StatusFilter)}
            className={`card-elevated p-3 flex items-center gap-3 hover:shadow-md transition-all text-left ${statusFilter === (s.label === 'Resolved Today' ? 'Resolved' : s.label) ? `border-2 ${s.border}` : ''}`}
          >
            <div className={`p-2 rounded-xl ${s.bg}`}>
              <span className={`w-2.5 h-2.5 rounded-full ${s.dot} inline-block`} />
            </div>
            <div>
              <div className={`text-[20px] font-700 font-tabular ${s.color}`}>{s.count}</div>
              <div className="text-[11px] text-muted-foreground">{s.label}</div>
            </div>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="card-elevated p-3 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Icon name="MagnifyingGlassIcon" size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search interactions, citizens, topics..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="input-field pl-8"
            />
          </div>

          {/* Channel tabs */}
          <div className="flex items-center gap-1 bg-muted/50 rounded-xl p-1">
            {(['All', 'Voice', 'WhatsApp', 'Live Chat', 'Email', 'Web Chat', 'GovChat'] as ChannelFilter[]).map(c => (
              <button
                key={c}
                onClick={() => setChannelFilter(c)}
                className={`px-2.5 py-1 rounded-lg text-[11px] font-600 transition-all whitespace-nowrap ${channelFilter === c ? 'bg-white text-primary shadow-sm border border-border' : 'text-muted-foreground hover:text-foreground'}`}
              >
                {c}
              </button>
            ))}
          </div>

          {/* Status filter */}
          <div className="flex items-center gap-1 bg-muted/50 rounded-xl p-1">
            {(['All', 'Active', 'Waiting', 'Resolved'] as StatusFilter[]).map(s => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-2.5 py-1 rounded-lg text-[11px] font-600 transition-all ${statusFilter === s ? 'bg-white text-primary shadow-sm border border-border' : 'text-muted-foreground hover:text-foreground'}`}
              >
                {s}
              </button>
            ))}
          </div>

          {(search || channelFilter !== 'All' || statusFilter !== 'All') && (
            <button
              onClick={() => { setSearch(''); setChannelFilter('All'); setStatusFilter('All'); }}
              className="btn-ghost px-2 py-1.5 text-[11px] flex items-center gap-1"
            >
              <Icon name="XMarkIcon" size={11} /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="card-elevated overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="bg-muted/40 border-b border-border">
                {[
                  { key: 'id', label: 'ID' },
                  { key: 'channel', label: 'Channel' },
                  { key: 'citizen', label: 'Citizen' },
                  { key: 'topic', label: 'Topic' },
                  { key: 'agency', label: 'Agency' },
                  { key: 'time', label: 'Time' },
                  { key: 'duration', label: 'Duration' },
                  { key: 'agent', label: 'Agent' },
                  { key: 'status', label: 'Status' },
                  { key: 'priority', label: 'Priority' },
                  { key: 'sentiment', label: 'Sentiment' },
                  { key: 'aiStatus', label: 'AI' },
                ].map(col => (
                  <th
                    key={col.key}
                    onClick={() => handleSort(col.key as keyof Interaction)}
                    className="text-left px-3 py-2.5 text-[10px] font-700 uppercase tracking-wide text-muted-foreground whitespace-nowrap cursor-pointer hover:text-foreground transition-colors select-none"
                  >
                    <div className="flex items-center gap-1">
                      {col.label}
                      {sortField === col.key ? (
                        <Icon name={sortDir === 'asc' ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={10} className="text-primary" />
                      ) : (
                        <Icon name="ChevronUpDownIcon" size={10} className="text-muted-foreground/40" />
                      )}
                    </div>
                  </th>
                ))}
                <th className="px-3 py-2.5 w-16" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={13} className="px-4 py-12 text-center">
                    <Icon name="InboxIcon" size={32} className="text-muted-foreground/30 mx-auto mb-2" />
                    <div className="text-[13px] font-600 text-muted-foreground">No interactions match the selected filters</div>
                    <div className="text-[11px] text-muted-foreground mt-1">Try adjusting your search or filter criteria</div>
                  </td>
                </tr>
              ) : sorted.map(item => (
                <tr
                  key={item.id}
                  className="table-row-hover transition-colors group cursor-pointer"
                  onClick={() => setSelectedInteraction(item)}
                >
                  <td className="px-3 py-2.5">
                    <button className="font-700 text-primary text-[11px] hover:underline">{item.id}</button>
                  </td>
                  <td className="px-3 py-2.5">
                    <ChannelBadge channel={item.channel} />
                  </td>
                  <td className="px-3 py-2.5 font-600 text-foreground whitespace-nowrap">{item.citizen}</td>
                  <td className="px-3 py-2.5 text-muted-foreground max-w-[160px] truncate">{item.topic}</td>
                  <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{item.agency}</td>
                  <td className="px-3 py-2.5 font-tabular text-muted-foreground whitespace-nowrap">{item.time}</td>
                  <td className="px-3 py-2.5 font-tabular text-muted-foreground whitespace-nowrap">{item.duration}</td>
                  <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{item.agent}</td>
                  <td className="px-3 py-2.5"><StatusBadge status={item.status} /></td>
                  <td className="px-3 py-2.5"><PriorityBadge priority={item.priority} /></td>
                  <td className="px-3 py-2.5"><SentimentBadge sentiment={item.sentiment} /></td>
                  <td className="px-3 py-2.5">
                    <span className={`text-[10px] font-600 px-1.5 py-0.5 rounded-md ${
                      item.aiStatus === 'Complete' ? 'bg-green-50 text-green-700 border border-green-200' :
                      item.aiStatus === 'Analysed'? 'bg-blue-50 text-blue-700 border border-blue-200' : 'bg-muted text-muted-foreground border border-border'
                    }`}>{item.aiStatus}</span>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Link href="/active-interaction-workspace">
                        <button className="p-1.5 rounded-lg hover:bg-muted transition-colors" title="Open">
                          <Icon name="ArrowTopRightOnSquareIcon" size={13} className="text-primary" />
                        </button>
                      </Link>
                      <button onClick={() => toast.info(`Viewing history for ${item.citizen}`)} className="p-1.5 rounded-lg hover:bg-muted transition-colors" title="History">
                        <Icon name="ClockIcon" size={13} className="text-muted-foreground" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/20">
          <div className="text-[11px] text-muted-foreground">
            Showing {sorted.length} of {allInteractions.length} interactions
          </div>
          <div className="flex items-center gap-1">
            {[1, 2, 3].map(p => (
              <button key={p} className={`w-7 h-7 rounded-lg text-[11px] font-600 transition-colors ${p === 1 ? 'bg-primary text-white' : 'hover:bg-muted text-muted-foreground'}`}>{p}</button>
            ))}
            <button className="w-7 h-7 rounded-lg text-[11px] hover:bg-muted text-muted-foreground transition-colors">
              <Icon name="ChevronRightIcon" size={12} className="mx-auto" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
